import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  serverTimestamp,
  setDoc,
  query,
  where,
} from 'firebase/firestore';
import { defaultTenantConfig } from '../config/tenant';
import { db } from './firebase';
import {
  DispatchEventType,
  DispatchOutboundSyncRecord,
  DispatchOutboundSyncStatus,
} from '../types/platform';

interface OutboundSyncConfig {
  enabled: boolean;
  endpoint?: string;
  authToken?: string;
  retrySeconds: number;
}

interface EnqueueOutboundDispatchSyncInput {
  tenantId: string;
  eventType: DispatchEventType;
  payload: Record<string, unknown>;
  actorName?: string;
  actorRole?: string;
  requestKey?: string;
  config?: Partial<OutboundSyncConfig>;
}

const safeRetrySeconds = (value?: number): number => {
  if (!Number.isFinite(value)) return 30;
  return Math.max(5, Math.min(600, Math.floor(value || 30)));
};

const buildOutboundConfig = (config?: Partial<OutboundSyncConfig>): OutboundSyncConfig => {
  return {
    enabled: config?.enabled === true,
    endpoint: config?.endpoint,
    authToken: config?.authToken,
    retrySeconds: safeRetrySeconds(config?.retrySeconds),
  };
};

const deriveRequestKey = (input: EnqueueOutboundDispatchSyncInput): string => {
  if (input.requestKey?.trim()) return input.requestKey.trim();
  return `${input.tenantId}:${input.eventType}:${Date.now()}`;
};

const nextRetryAtIso = (retrySeconds: number): string => {
  return new Date(Date.now() + retrySeconds * 1000).toISOString();
};

const writeSyncState = async (
  recordId: string,
  update: Partial<DispatchOutboundSyncRecord>
): Promise<void> => {
  await setDoc(
    doc(db, 'dispatch_outbound_sync', recordId),
    {
      ...update,
      updatedAt: new Date().toISOString(),
      updatedAtServer: serverTimestamp(),
    },
    { merge: true }
  );
};

const attemptOutboundDispatchSync = async (recordId: string, input: {
  endpoint: string;
  authToken?: string;
  retrySeconds: number;
  payload: Record<string, unknown>;
  attemptCount: number;
}): Promise<void> => {
  try {
    const response = await fetch(input.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(input.authToken ? { Authorization: `Bearer ${input.authToken}` } : {}),
      },
      body: JSON.stringify(input.payload),
    });

    if (response.ok) {
      await writeSyncState(recordId, {
        status: 'SENT',
        responseCode: response.status,
        attemptCount: input.attemptCount,
        lastError: '',
        nextRetryAt: '',
      });
      return;
    }

    await writeSyncState(recordId, {
      status: 'FAILED',
      responseCode: response.status,
      attemptCount: input.attemptCount,
      lastError: `HTTP ${response.status}`,
      nextRetryAt: nextRetryAtIso(input.retrySeconds),
    });
  } catch (error: any) {
    await writeSyncState(recordId, {
      status: 'FAILED',
      attemptCount: input.attemptCount,
      lastError: (error?.message || 'Outbound sync request failed').slice(0, 300),
      nextRetryAt: nextRetryAtIso(input.retrySeconds),
    });
  }
};

export const enqueueOutboundDispatchSync = async (
  input: EnqueueOutboundDispatchSyncInput
): Promise<{ id: string; status: DispatchOutboundSyncStatus }> => {
  const nowIso = new Date().toISOString();
  const syncRef = doc(collection(db, 'dispatch_outbound_sync'));
  const config = buildOutboundConfig(input.config);
  const requestKey = deriveRequestKey(input);

  const initialStatus: DispatchOutboundSyncStatus =
    config.enabled && config.endpoint ? 'QUEUED' : 'PENDING_CONFIG';

  const baseRecord: Omit<DispatchOutboundSyncRecord, 'id'> = {
    tenantId: input.tenantId,
    eventType: input.eventType,
    status: initialStatus,
    payload: {
      requestKey,
      tenantId: input.tenantId,
      eventType: input.eventType,
      actorName: input.actorName || null,
      actorRole: input.actorRole || null,
      sentAt: nowIso,
      ...input.payload,
    },
    attemptCount: 0,
    requestUrl: config.endpoint || '',
    responseCode: 0,
    lastError: initialStatus === 'PENDING_CONFIG' ? 'Outbound sync endpoint/config not enabled' : '',
    nextRetryAt: '',
    createdAt: nowIso,
    updatedAt: nowIso,
  };

  await setDoc(doc(db, 'dispatch_outbound_sync', syncRef.id), {
    ...baseRecord,
    createdAtServer: serverTimestamp(),
    updatedAtServer: serverTimestamp(),
  });

  if (initialStatus === 'QUEUED' && config.endpoint) {
    await attemptOutboundDispatchSync(syncRef.id, {
      endpoint: config.endpoint,
      authToken: config.authToken,
      retrySeconds: config.retrySeconds,
      payload: baseRecord.payload,
      attemptCount: 1,
    });
  }

  return { id: syncRef.id, status: initialStatus };
};

export const retryOutboundDispatchSync = async (input: {
  recordId: string;
  config?: Partial<OutboundSyncConfig>;
}): Promise<void> => {
  const recordRef = doc(db, 'dispatch_outbound_sync', input.recordId);
  const snap = await getDoc(recordRef);
  if (!snap.exists()) throw new Error('Outbound sync record not found.');

  const record = snap.data() as Omit<DispatchOutboundSyncRecord, 'id'>;
  const fallbackConfig = {
    enabled: defaultTenantConfig.dispatchIntegration.outboundSyncEnabled === true,
    endpoint: defaultTenantConfig.dispatchIntegration.outboundSyncEndpoint,
    authToken: defaultTenantConfig.dispatchIntegration.outboundSyncAuthToken,
    retrySeconds: safeRetrySeconds(defaultTenantConfig.dispatchIntegration.outboundSyncRetrySeconds),
  };

  const mergedConfig = buildOutboundConfig({
    ...fallbackConfig,
    ...input.config,
    endpoint: input.config?.endpoint || record.requestUrl || fallbackConfig.endpoint,
  });

  if (!mergedConfig.enabled || !mergedConfig.endpoint) {
    await writeSyncState(input.recordId, {
      status: 'PENDING_CONFIG',
      lastError: 'Retry blocked: outbound sync endpoint/config not enabled',
    });
    return;
  }

  await writeSyncState(input.recordId, {
    status: 'QUEUED',
    lastError: '',
  });

  await attemptOutboundDispatchSync(input.recordId, {
    endpoint: mergedConfig.endpoint,
    authToken: mergedConfig.authToken,
    retrySeconds: mergedConfig.retrySeconds,
    payload: record.payload,
    attemptCount: (record.attemptCount || 0) + 1,
  });
};

export const subscribeOutboundDispatchSync = (
  tenantId: string,
  onData: (records: DispatchOutboundSyncRecord[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const q = query(
    collection(db, 'dispatch_outbound_sync'),
    where('tenantId', '==', tenantId),
    orderBy('updatedAt', 'desc'),
    limit(50)
  );

  return onSnapshot(
    q,
    (snap) => {
      const records = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<DispatchOutboundSyncRecord, 'id'>),
      }));
      onData(records);
    },
    (error) => onError?.(error)
  );
};
